import { ModuleFederationToolsModule } from '@angular-architects/module-federation-tools';
import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    WellSchematic,
    WellSchematicCdft,
} from 'app/modules/well-schematic-diagram/model';
import { WellSchematicDiagramService } from 'app/modules/well-schematic-diagram/services/well-schematic-diagram.service';
import { WellSchematicDiagramState } from 'app/modules/well-schematic-diagram/state/well-schematic-diagram.state';
import module from 'app/modules/well-schematic-diagram/well-schematic-diagram.module';
import { environment } from 'environments/environment';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-view',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [...module, ModuleFederationToolsModule],
    templateUrl: './history.page.html',
    styleUrl: './history.page.scss',
})
export class HistoryPage {
    isApplication: boolean;
    applicationId: string;
    webComponent;
    wellStringId;
    schematic: WellSchematic;
    cdftSchematic: WellSchematicCdft[];
    configurationHistory: any; // Add this property to store history data
    constructor(
        private route: ActivatedRoute,
        private service: WellSchematicDiagramService,
        private state: WellSchematicDiagramState
    ) {}

    async ngOnInit() {
        this.isApplication =
            this.route.snapshot.paramMap.get('type') != 'configuration';
        this.applicationId = this.route.snapshot.paramMap.get('type');
        this.wellStringId = this.route.snapshot.paramMap.get('wellStringId');
        this.schematic = await this.getSchematic(this.wellStringId);
        this.configurationHistory = await this.fetchConfigurationHistory(
            this.wellStringId
        ); // Fetch history data
        this.initiateComponent();
    }

    getSchematic(wellStringId, force = false): Promise<WellSchematic> {
        return new Promise((resolve, reject) => {
            if (!this.state.isEmpty() && !force) {
                resolve(this.state.findByWellStringId(wellStringId));
            }

            this.service
                .getWellConfigList({ Search: { wellStringId } })
                .subscribe((response) => {
                    resolve(response.data.data[0]);
                });
        });
    }

    fetchConfigurationHistory(wellStringId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.service.getConfigurationHistory(wellStringId).subscribe({
                next: (response) => resolve(response.data),
                error: (error) => reject(new Error(error)),
            });
        });
    }

    initiateComponent() {
        this.webComponent = {
            options: {
                type: 'script',
                remoteEntry: `${environment.mfeReactUrl}`,
                remoteName: 'react',
                exposedModule: './well-barrier-history',
                elementName: 'well-barrier-history',
            },
            props: {
                schematic: this.schematic,
                isApplication: this.isApplication,
            },
            events: {
                create: (e) => this.create(e.detail),
                getAllConfig: (e) => this.getAllConfig(e.detail),
                update: (e) => this.update(e.detail),
                delete: (e) => this.delete(e.detail),
                assign: (e) => this.assign(e.detail),
                status: (e) => this.status(e.detail),
                getCdft: (e) => this.getCdft(e.detail),
                getConfigurationHistory: (e) =>
                    this.getConfigurationHistory(e.detail),
            },
        };
    }

    status(event) {
        const payload = {
            ...event.payload,
            id: this.applicationId,
            remark: '',
            wellStringId: this.wellStringId,
        };
        this.service.saveApplicationStatus(payload).subscribe((response) => {
            event.resolve(response);
        });
    }

    create(event) {
        this.service.saveWellConfig(event.payload).subscribe((response) => {
            event.resolve(response);
        });
    }

    update(event) {
        this.service.updateWellConfig(event.payload).subscribe((response) => {
            event.resolve(response);
        });
    }

    delete(event) {
        this.service
            .deleteWellConfig(event.payload.id)
            .subscribe((response) => {
                event.resolve(response);
            });
    }

    assign(event) {
        this.service
            .assignSchematic(this.schematic.wellStringId, event.payload.id)
            .subscribe(async (response) => {
                this.schematic = await this.getSchematic(
                    this.wellStringId,
                    true
                );
                event.resolve(response);
            });
    }

    getAllConfig(event) {
        const params = {
            wellStringId: this.wellStringId,
            isApplication: this.isApplication ? 'true' : 'false',
        };
        const allConfigsParams = {
            PageSize: event.payload?.PageSize ?? '',
            Page: event.payload?.Page ?? '',
            TotalPage: event.payload?.totalPages ?? '',
            Search: event.payload?.Search ?? {},
        };
        forkJoin({
            selected: this.service.getWellSchematicByWellString(params),
            all: this.service.getAllConfig(allConfigsParams),
        }).subscribe(({ selected, all }) => {
            const configs = {
                selectedConfig: selected.data,
                allConfig: all.data,
                isApplication: this.isApplication,
                schematic: this.schematic,
            };
            event.resolve(configs);
        });
    }

    getCdft(event) {
        if (this.cdftSchematic) {
            return event.resolve(this.cdftSchematic);
        }

        this.service
            .getCdftSchematic(this.schematic.wellStringId)
            .subscribe((response) => {
                event.resolve(response.data);
            });
    }

    getConfigurationHistory(event) {
        this.service
            .getConfigurationHistory(this.schematic.wellStringId)
            .subscribe((response) => {
                event.resolve(response.data);
            });
    }
}
